import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { GoalTemplate, Matter, Project } from '../../lib/db';

// Props interface for the GoalTemplateEditModal component
// template: The template being used to create a deliverable (null if modal closed)
// isOpen: Whether the modal is currently visible
// onClose: Function called when user closes the modal without creating
// onConfirm: Function called when user submits the form with customized data
// matters: List of available matters to choose from
// projects: List of available projects to choose from
// defaultMatterId: Pre-selected matter ID (optional)
// defaultProjectId: Pre-selected project ID (optional)
interface GoalTemplateEditModalProps {
  template: GoalTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    title: string;
    description?: string;
    category?: string;
    priority: number;
    start_date?: string;
    deadline?: string;
    matter_id?: string;
    project_id?: string;
  }) => void;
  matters: Matter[];
  projects: Project[];
  defaultMatterId?: string;
  defaultProjectId?: string;
}

/**
 * GoalTemplateEditModal Component
 * 
 * This modal allows users to customize a deliverable template before creating it.
 * When a user selects a template, they can modify the details to fit their specific needs.
 * 
 * Features:
 * 1. Pre-populates form with template data
 * 2. Allows editing of all deliverable fields (title, description, dates, etc.)
 * 3. Project/matter selection with filtering (matters filtered by selected project)
 * 4. Smart defaults (today's date for start, 30 days out for deadline)
 * 5. Form validation for required fields
 * 6. Responsive modal design
 * 
 * Workflow:
 * 1. User selects a template from the template library
 * 2. This modal opens with template data pre-filled
 * 3. User can customize any field (title, dates, project assignment, etc.)
 * 4. User clicks "Create Deliverable" to create with their customizations
 * 5. Or user clicks "Cancel" to abort without creating
 */
export function GoalTemplateEditModal({ 
  template, 
  isOpen, 
  onClose, 
  onConfirm,
  matters,
  projects,
  defaultMatterId,
  defaultProjectId
}: GoalTemplateEditModalProps) {
  // Form field state variables
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState(5);
  const [startDate, setStartDate] = useState('');
  const [deadline, setDeadline] = useState('');
  const [selectedMatterId, setSelectedMatterId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');

  // Effect to populate form fields when a template is selected or defaults change
  useEffect(() => {
    if (template) {
      // Populate basic fields from template
      setTitle(template.title);
      setDescription(template.description || '');
      setCategory(template.category || '');
      setPriority(5); // Default priority for new deliverables
      
      // Set smart date defaults: start today, deadline in 30 days
      const today = new Date();
      const thirtyDaysFromNow = new Date(today);
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      
      setStartDate(today.toISOString().split('T')[0]);
      setDeadline(thirtyDaysFromNow.toISOString().split('T')[0]);
      
      // Apply any pre-selected matter/project (e.g., from current context)
      setSelectedMatterId(defaultMatterId || '');
      setSelectedProjectId(defaultProjectId || '');
    }
  }, [template, defaultMatterId, defaultProjectId]);

  // Handler for form submission - creates the deliverable with customized data
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Pass the form data back to parent, converting empty strings to undefined
    onConfirm({
      title,
      description: description || undefined,
      category: category || undefined,
      priority,
      start_date: startDate || undefined,
      deadline: deadline || undefined,
      matter_id: selectedMatterId || undefined,
      project_id: selectedProjectId || undefined,
    });
  };
  
  // Filter matters to only show those belonging to the selected project
  // If no project is selected, show all matters
  const filteredMatters = selectedProjectId 
    ? matters.filter(m => m.project_id === selectedProjectId)
    : matters;

  // Don't render anything if modal is closed or no template selected
  if (!isOpen || !template) return null;

  return (
    // Modal overlay covering entire screen
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Semi-transparent backdrop - clicking closes modal */}
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      
      {/* Modal content container - centered on screen */}
      <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 max-w-lg mx-auto bg-white rounded-lg shadow-xl">
        {/* Modal header with title and close button */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Customize Deliverable</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Edit the details before creating your deliverable
          </p>
        </div>

        {/* Main form content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title field (required) */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category (Optional)
            </label>
            <input
              type="text"
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
              Priority (1-10)
            </label>
            <input
              type="number"
              id="priority"
              min="1"
              max="10"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value) || 5)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date (Optional)
              </label>
              <input
                type="date"
                id="start_date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-1">
                Deadline (Optional)
              </label>
              <input
                type="date"
                id="deadline"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                min={startDate}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="project" className="block text-sm font-medium text-gray-700 mb-1">
                Project (Optional)
              </label>
              <select
                id="project"
                value={selectedProjectId}
                onChange={(e) => {
                  setSelectedProjectId(e.target.value);
                  // Clear matter selection if it doesn't belong to the newly selected project
                  // This prevents invalid project/matter combinations
                  if (e.target.value && selectedMatterId) {
                    const matter = matters.find(m => m.id === selectedMatterId);
                    if (matter && matter.project_id !== e.target.value) {
                      setSelectedMatterId('');
                    }
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No Project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="matter" className="block text-sm font-medium text-gray-700 mb-1">
                Matter (Optional)
              </label>
              <select
                id="matter"
                value={selectedMatterId}
                onChange={(e) => setSelectedMatterId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No Matter</option>
                {filteredMatters.map(matter => (
                  <option key={matter.id} value={matter.id}>
                    {matter.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Form action buttons */}
          <div className="flex justify-end gap-3 pt-4">
            {/* Cancel button - closes modal without creating */}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            {/* Submit button - creates deliverable with form data */}
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Create Deliverable
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}